import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import { apiRequest } from '../config/api';

export default function useSettings() {
  const [showDarkModeWarning, setShowDarkModeWarning] = useState(false);
  const [darkModeWarned, setDarkModeWarned] = useState(false);
  const { user, organization, logout, switchOrganization, token, updateOrganization, setUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const { darkMode, toggleDarkMode } = useTheme();

  const handleDarkModeToggle = () => {
    if (!darkMode && !darkModeWarned) {
      setShowDarkModeWarning(true);
    } else {
      toggleDarkMode();
    }
  };

  const [organizations, setOrganizations] = useState([]);
  const [orgSettings, setOrgSettings] = useState({
    name: organization?.name || '',
    description: organization?.description || '',
  });
  const [userSettings, setUserSettings] = useState({
    username: user?.username || '',
    full_name: user?.full_name || '',
  });
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingAccessRequests, setPendingAccessRequests] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [objectDefinitions, setObjectDefinitions] = useState([]);

  useEffect(() => {
    if (organization) {
      setOrgSettings({
        name: organization.name,
        description: organization.description || '',
      });
    }
  }, [organization]);

  useEffect(() => {
    if (user) {
      setUserSettings({
        username: user.username || '',
        full_name: user.full_name || '',
      });
    }
  }, [user]);

  useEffect(() => {
    fetchUserOrganizations();
    fetchOrganizationMembers();
    fetchPendingInvites();
    fetchPendingAccessRequests();
    fetchObjectDefinitions();
  }, []);

  const fetchUserOrganizations = async () => {
    try {
      const response = await apiRequest('/auth/organizations');
      if (response.ok) {
        const orgs = await response.json();
        setOrganizations(orgs);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchOrganizationMembers = async () => {
    try {
      const response = await apiRequest('/organizations/current/members');
      if (response.ok) {
        const membersList = await response.json();
        setMembers(membersList);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await apiRequest('/organizations/current/invites');
      if (response.ok) {
        const invites = await response.json();
        setPendingInvites(invites);
      }
    } catch (error) {
      console.error('Error fetching pending invites:', error);
    }
  };

  const fetchPendingAccessRequests = async () => {
    try {
      // Only org owners can fetch this; non-owners will get 403 which we silently ignore
      const response = await apiRequest('/organizations/current/access-requests');
      if (response.ok) {
        const requests = await response.json();
        setPendingAccessRequests(requests);
      }
    } catch (error) {
      console.error('Error fetching pending access requests:', error);
    }
  };

  const handleReviewAccessRequest = async (requestId, action) => {
    try {
      const response = await apiRequest(`/organizations/current/access-requests/${requestId}/review`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      if (response.ok) {
        showSuccess(action === 'approve' ? 'Access request approved' : 'Access request denied');
        fetchPendingAccessRequests();
        if (action === 'approve') {
          fetchOrganizationMembers();
        }
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to review access request');
      }
    } catch (error) {
      showError('Error reviewing access request');
    }
  };

  const fetchObjectDefinitions = async () => {
    try {
      const response = await apiRequest('/crm/objects/');
      if (response.ok) {
        const defs = await response.json();
        setObjectDefinitions(defs);
      }
    } catch (error) {
      console.error('Error fetching object definitions:', error);
    }
  };

  const handleUserUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiRequest('/auth/me', {
        method: 'PUT',
        body: JSON.stringify(userSettings),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setIsEditingUser(false);
        showSuccess('Profile updated successfully');
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      showError('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUserEdit = () => {
    setUserSettings({
      username: user?.username || '',
      full_name: user?.full_name || '',
    });
    setIsEditingUser(false);
  };

  const handleOrgUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiRequest('/organizations/current', {
        method: 'PUT',
        body: JSON.stringify(orgSettings),
      });

      if (response.ok) {
        await updateOrganization();
        showSuccess('Organization settings updated successfully');
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to update organization');
      }
    } catch (error) {
      showError('Error updating organization');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiRequest('/organizations/current/invite', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail }),
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(result.message, 5000);
        setInviteEmail('');
        if (result.user_exists) {
          fetchOrganizationMembers();
        } else {
          fetchPendingInvites();
        }
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to invite user');
      }
    } catch (error) {
      showError('Error inviting user');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/organizations/current/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showSuccess('Member removed successfully');
        fetchOrganizationMembers();
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to remove member');
      }
    } catch (error) {
      showError('Error removing member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (inviteId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;

    try {
      const response = await apiRequest(`/organizations/current/invites/${inviteId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSuccess('Invitation cancelled successfully');
        fetchPendingInvites();
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to cancel invitation');
      }
    } catch (error) {
      showError('Error cancelling invitation');
    }
  };

  const confirmDarkMode = () => {
    setShowDarkModeWarning(false);
    setDarkModeWarned(true);
    toggleDarkMode();
  };

  return {
    // State
    objectDefinitions,
    showDarkModeWarning,
    darkMode,
    organizations,
    orgSettings,
    userSettings,
    members,
    pendingInvites,
    pendingAccessRequests,
    inviteEmail,
    loading,
    isEditingUser,
    activeTab,
    user,
    organization,
    // Setters
    setShowDarkModeWarning,
    setOrgSettings,
    setUserSettings,
    setInviteEmail,
    setIsEditingUser,
    setActiveTab,
    // Actions
    handleDarkModeToggle,
    confirmDarkMode,
    handleUserUpdate,
    handleCancelUserEdit,
    handleOrgUpdate,
    handleInviteUser,
    handleRemoveMember,
    handleCancelInvitation,
    handleReviewAccessRequest,
    switchOrganization,
    updateOrganization,
    fetchObjectDefinitions,
    logout,
  };
}
